import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "@mui/material";
import { toast } from "react-toastify";
import { SearchIcon, PlusIcon, EditIcon, TrashIcon, EyeIcon, CloseIcon } from "../../icons";
import { buttons, colors, gradients, shadows, bgcolors, textcolors } from "../../theme";
import {
  listFaceEmbeddings,
  enrollFace,
  deleteFaceEmbedding,
  updateFaceEmbeddingMeta,
  updateFaceEmbeddingPhotos,
  getFaceEmbedding,
  clearSelected,
} from "../../features/faceEmbeddings/faceEmbeddingSlice";

const BASE_URL = process.env.REACT_APP_BASE_URL || "";
const buildFaceImageUrl = (ref, tenantId) =>
  `${BASE_URL}/api/v1/tenants/${tenantId}/face-embeddings/image/${ref.replace(/\\/g, "/")}`;

const ALLOWED_TYPES = ["image/jpeg", "image/png"];
const MAX_FILE_MB   = 5;

// ─── Grey user icon (shown only when a photo EXISTS but returns 404) ─────────
const GreyUserIcon = () => (
  <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: colors.primaryLight }}>
    <svg viewBox="0 0 24 24" fill="none" className="w-1/2 h-1/2" style={{ color: colors.primary }}>
      <circle cx="12" cy="8" r="4" fill="currentColor" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" fill="currentColor" />
    </svg>
  </div>
);

// ─── Empty slot (shown for angles that have no photo uploaded yet) ────────────
const EmptyPhotoSlot = () => (
  <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: colors.bg }}>
    <svg viewBox="0 0 24 24" fill="none" className="w-1/3 h-1/3" style={{ color: colors.border2 }}>
      <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2" />
      <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const EditIcon2 = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

// ─── Constants ────────────────────────────────────────────────────────────────
const AVATAR_COLORS = [colors.primary, colors.indigo, colors.violet, colors.accent, colors.emerald, colors.warning];
const PHOTO_ANGLES  = ["Front", "Left", "Right", "Back", "Top"];
const MAX_PHOTOS    = 5;

// ─── Shared styles ────────────────────────────────────────────────────────────
const inputStyle = { backgroundColor: colors.bg, border: `1px solid ${colors.border}`, color: colors.text };
const inputCls   = "w-full rounded-xl py-2.5 px-3 text-sm outline-none transition-all";
const onFocus    = (e) => (e.currentTarget.style.borderColor = colors.primary);
const onBlur     = (e) => (e.currentTarget.style.borderColor = colors.border);

const FieldLabel = ({ children, required }) => (
  <label className="block text-sm font-semibold mb-1.5" style={{ color: colors.text }}>
    {children}{required && <span style={{ color: colors.danger }}> *</span>}
  </label>
);

// ─── Photo upload grid (shared by Add & Edit) ─────────────────────────────────
// photos: Array<{ file: File|null, preview: string, existing: boolean }>
const PhotoUploadGrid = ({ photos, setPhotos }) => {
  const fileInputRef = React.useRef(null);
  const [failedIdx, setFailedIdx] = useState({});

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = MAX_PHOTOS - photos.length;

    const valid = files.slice(0, remaining).filter((file) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: only JPG and PNG are allowed`);
        return false;
      }
      if (file.size > MAX_FILE_MB * 1024 * 1024) {
        toast.error(`${file.name}: file must be under ${MAX_FILE_MB}MB`);
        return false;
      }
      return true;
    });

    const toAdd = valid.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      existing: false,
    }));
    setPhotos((prev) => [...prev, ...toAdd]);
    setFailedIdx({});
    e.target.value = "";
  };

  const removePhoto = (idx) => {
    setFailedIdx({});
    setPhotos((prev) => {
      const item = prev[idx];
      if (!item.existing && item.preview.startsWith("blob:")) {
        URL.revokeObjectURL(item.preview);
      }
      return prev.filter((_, i) => i !== idx);
    });
  };

  return (
    <div>
      <p className="text-sm font-semibold mb-2" style={{ color: colors.text }}>
        Photos{" "}
        <span className="font-normal" style={{ color: colors.primary }}>
          — upload 4 to 5 multi-angle images
        </span>
      </p>
      <div
        className="rounded-xl p-3"
        style={{ border: `2px dashed ${colors.border2}`, backgroundColor: `${colors.primary}08` }}
      >
        <div className="flex flex-wrap gap-2">
          {photos.map((p, idx) => {
            const label = PHOTO_ANGLES[idx] || null;
            return (
              <div
                key={idx}
                className="relative w-[72px] h-[72px] rounded-lg overflow-hidden shrink-0"
                style={{ border: `1px solid ${colors.border}` }}
              >
                {p.preview && !failedIdx[idx]
                  ? <img
                      src={p.preview}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={() => setFailedIdx((prev) => ({ ...prev, [idx]: true }))}
                    />
                  : <GreyUserIcon />
                }
                {label && (
                  <div className="absolute bottom-0 left-0 right-0 px-1 py-0.5 flex items-center gap-0.5" style={{ background: colors.overlay }}>
                    <span className={`text-[8px] font-black tracking-widest uppercase ${textcolors.white} opacity-70`}>{label.slice(0, 1)}</span>
                    <span className={`text-[9px] font-semibold ${textcolors.white}`}>{label}</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removePhoto(idx)}
                  className={`absolute top-0.5 right-0.5 w-4 h-4 ${bgcolors.backdropMid} rounded-full ${textcolors.white} text-[10px] flex items-center justify-center leading-none`}
                >×</button>
              </div>
            );
          })}
          {photos.length < MAX_PHOTOS && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-[72px] h-[72px] flex flex-col items-center justify-center gap-1 rounded-lg transition-colors shrink-0"
              style={{ border: `2px dashed ${colors.border2}`, backgroundColor: colors.panel, color: colors.primary }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.bg)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = colors.panel)}
            >
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 16 12 12 8 16" />
                <line x1="12" y1="12" x2="12" y2="21" />
                <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3" />
              </svg>
              <span className="text-[11px] font-semibold">Upload</span>
            </button>
          )}
        </div>
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png" multiple className="hidden" onChange={handleFileChange} />
        <div className="flex justify-between mt-2 text-xs" style={{ color: colors.textMute }}>
          <span>{photos.length} / {MAX_PHOTOS} uploaded</span>
          <span>JPG, PNG up to {MAX_FILE_MB}MB each</span>
        </div>
      </div>
    </div>
  );
};

// ─── Shared modal shell ───────────────────────────────────────────────────────
const ModalShell = ({ title, onClose, onSubmit, submitLabel, children }) => (
  <div className={`fixed inset-0 z-50 flex items-center justify-center ${bgcolors.overlay} backdrop-blur-sm p-4`}>
    <div
      className="w-full max-w-xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden"
      style={{ backgroundColor: colors.panel, border: `1px solid ${colors.border}` }}
    >
      <div className="h-1 w-full shrink-0" style={{ background: gradients.accent }} />
      <div className="flex items-center justify-between px-6 py-5 shrink-0" style={{ borderBottom: `1px solid ${colors.border}` }}>
        <h2 className="text-lg font-bold" style={{ color: colors.text }}>{title}</h2>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors"
          style={{ color: colors.textDim }}
          onMouseEnter={(e) => { e.currentTarget.style.color = colors.text; e.currentTarget.style.backgroundColor = colors.bg2; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = colors.textDim; e.currentTarget.style.backgroundColor = "transparent"; }}
        >
          <CloseIcon size={20} />
        </button>
      </div>

      <form
        onSubmit={onSubmit}
        className="overflow-y-auto px-6 py-5 flex flex-col gap-5 flex-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      >
        {children}
      </form>

      <div className="flex items-center justify-end gap-3 px-6 py-4 shrink-0" style={{ borderTop: `1px solid ${colors.border}`, backgroundColor: colors.panel }}>
        <button
          type="button" onClick={onClose}
          className="px-5 py-2.5 rounded-xl text-sm font-medium transition-colors"
          style={{ backgroundColor: colors.bg2, color: colors.textDim, border: `1px solid ${colors.border}` }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.border)}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = colors.bg2)}
        >
          Cancel
        </button>
        <button
          type="submit" onClick={onSubmit}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold ${buttons.primary}`}
        >
          <CheckIcon />
          {submitLabel}
        </button>
      </div>
    </div>
  </div>
);

// ─── Shared form fields ───────────────────────────────────────────────────────
const PersonFormFields = ({ form, set }) => (
  <>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <FieldLabel required>Full Name</FieldLabel>
        <input className={inputCls} style={inputStyle} placeholder="e.g. Anjali Krishnan" value={form.name} onChange={set("name")} onFocus={onFocus} onBlur={onBlur} required />
      </div>
      <div>
        <FieldLabel required>Register Number / ID</FieldLabel>
        <input className={inputCls} style={inputStyle} placeholder="e.g. EMP-1042" value={form.registerId} onChange={set("registerId")} onFocus={onFocus} onBlur={onBlur} required />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <FieldLabel>Email</FieldLabel>
        <input type="email" className={inputCls} style={inputStyle} placeholder="name@steppingedge.com" value={form.email} onChange={set("email")} onFocus={onFocus} onBlur={onBlur} />
      </div>
      <div>
        <FieldLabel>Phone</FieldLabel>
        <input type="tel" className={inputCls} style={inputStyle} placeholder="+91 98765 43210" value={form.phone} onChange={set("phone")} onFocus={onFocus} onBlur={onBlur} />
      </div>
    </div>
  </>
);

// ─── Add Person Modal ─────────────────────────────────────────────────────────
const AddPersonModal = ({ onClose, onAdd }) => {
  const [form, setForm]     = useState({ name: "", registerId: "", email: "", phone: "" });
  const [photos, setPhotos] = useState([]);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.registerId.trim()) return;
    if (photos.length === 0) { toast.error("Please upload at least 1 photo."); return; }
    onAdd({ ...form, photos: photos.map((p) => p.file) });
    onClose();
  };

  return (
    <ModalShell title="Add People" onClose={onClose} onSubmit={handleSubmit} submitLabel="Save Person">
      <PhotoUploadGrid photos={photos} setPhotos={setPhotos} />
      <PersonFormFields form={form} set={set} />
    </ModalShell>
  );
};

// ─── Edit Person Modal ────────────────────────────────────────────────────────
const EditPersonModal = ({ person, onClose, onEdit, tenantId }) => {
  const [form, setForm] = useState({
    name:       person.name         || "",
    registerId: person.employee_id  || "",
    email:      person.email        || "",
    phone:      person.phone_number || "",
  });

  // Pre-load existing photos; new uploads are appended with existing: false
  const [photos, setPhotos] = useState(
    (person.source_image_refs || []).map((ref) => ({
      file:     null,
      preview:  buildFaceImageUrl(ref, tenantId),
      existing: true,
      ref,
    }))
  );

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.registerId.trim()) return;
    const newFiles      = photos.filter((p) => !p.existing).map((p) => p.file);
    const remainingRefs = photos.filter((p) => p.existing).map((p) => p.ref);
    onEdit({ ...person, ...form, photos: newFiles, remainingRefs });
    onClose();
  };

  return (
    <ModalShell title="Edit People" onClose={onClose} onSubmit={handleSubmit} submitLabel="Save Changes">
      <PhotoUploadGrid photos={photos} setPhotos={setPhotos} />
      <PersonFormFields form={form} set={set} />
    </ModalShell>
  );
};

// ─── View Person Modal ────────────────────────────────────────────────────────
const ViewPersonModal = ({ person, onClose, onEditOpen }) => {
  const dispatch    = useDispatch();
  const tenantId    = localStorage.getItem("tenant_id");
  const { selected, isLoading } = useSelector((state) => state.faceEmbeddings);

  // Fetch fresh data by ID when modal opens
  useEffect(() => {
    dispatch(getFaceEmbedding({ tenantId, faceEmbeddingId: person.id }));
  }, [dispatch, tenantId, person.id]);

  // Use fresh fetched data once available, fall back to list data while loading
  const data        = selected?.id === person.id ? selected : person;
  const avatarColor = AVATAR_COLORS[(data.id - 1) % AVATAR_COLORS.length];
  const photos      = (data.source_image_refs || []).map((ref) => buildFaceImageUrl(ref, tenantId));
  const [failedIdx, setFailedIdx] = useState({});

  // Reset failedIdx whenever source_image_refs changes (e.g. after edit)
  const refsKey = (data.source_image_refs || []).join(",");
  useEffect(() => { setFailedIdx({}); }, [refsKey]);

  const SectionLabel = ({ children }) => (
    <p className="text-[11px] font-bold tracking-widest uppercase mb-3" style={{ color: colors.textMute }}>{children}</p>
  );

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${bgcolors.overlay} backdrop-blur-sm p-4`}>
      <div
        className="w-full max-w-lg rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden"
        style={{ backgroundColor: colors.panel, border: `1px solid ${colors.border}` }}
      >
        <div className="h-1 w-full shrink-0" style={{ background: gradients.accent }} />
        <div className="flex items-center justify-between px-6 py-5 shrink-0" style={{ borderBottom: `1px solid ${colors.border}` }}>
          <h2 className="text-lg font-bold" style={{ color: colors.text }}>Profile Details</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors"
            style={{ color: colors.textDim }}
            onMouseEnter={(e) => { e.currentTarget.style.color = colors.text; e.currentTarget.style.backgroundColor = colors.bg2; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = colors.textDim; e.currentTarget.style.backgroundColor = "transparent"; }}
          >
            <CloseIcon size={20} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 flex flex-col gap-6 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full flex items-center justify-center text-white text-lg font-bold shrink-0" style={{ background: avatarColor }}>
              {(data.name || "?").split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("")}
            </div>
            <div>
              <p className="text-base font-bold" style={{ color: colors.text }}>{data.name}</p>
              <p className="text-sm" style={{ color: colors.textDim }}>{data.employee_id}</p>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <SectionLabel>Multi-Angle Photos</SectionLabel>
              <span className="text-xs font-semibold" style={{ color: colors.textMute }}>
                {(data.source_image_refs || []).length} / {MAX_PHOTOS} photos
              </span>
            </div>
            {isLoading && selected?.id !== data.id && (
              <p className="text-xs mb-2" style={{ color: colors.textMute }}>Loading latest photos...</p>
            )}
            <div className="grid grid-cols-4 gap-2">
              {PHOTO_ANGLES.map((angle, idx) => (
                <div key={angle} className="relative rounded-xl overflow-hidden aspect-square" style={{ border: `1px solid ${colors.border}` }}>
                  {photos[idx]
                    ? failedIdx[idx]
                      ? <GreyUserIcon />   /* photo exists but 404 */
                      : <img
                          src={photos[idx]}
                          alt={angle}
                          className="w-full h-full object-cover"
                          onError={() => setFailedIdx((prev) => ({ ...prev, [idx]: true }))}
                        />
                    : <EmptyPhotoSlot />   /* no photo uploaded for this angle */
                  }
                  <div className="absolute bottom-0 left-0 right-0 px-1.5 py-1 flex items-center gap-1" style={{ background: colors.overlay }}>
                    <span className={`text-[9px] font-black tracking-widest uppercase ${textcolors.white} opacity-70`}>{angle.slice(0, 1)}</span>
                    <span className={`text-[10px] font-semibold ${textcolors.white}`}>{angle}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <SectionLabel>Profile Details</SectionLabel>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              {[
                { label: "Email", value: data.email        || "—" },
                { label: "Phone", value: data.phone_number || "—" },
              ].map(({ label, value }) => (
                <div key={label}>
                  <p className="text-[10px] font-bold tracking-widest uppercase mb-0.5" style={{ color: colors.textMute }}>{label}</p>
                  <p className="text-sm font-medium" style={{ color: colors.text }}>{value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 shrink-0" style={{ borderTop: `1px solid ${colors.border}`, backgroundColor: colors.panel }}>
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl text-sm font-medium transition-colors"
            style={{ backgroundColor: colors.bg2, color: colors.textDim, border: `1px solid ${colors.border}` }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.border)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = colors.bg2)}
          >
            Close
          </button>
          <button
            onClick={() => { onClose(); onEditOpen(person); }}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold ${buttons.primary}`}
          >
            <EditIcon2 />
            Edit
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── People Management Page ───────────────────────────────────────────────────
export default function PeopleManagement() {
  const dispatch   = useDispatch();
  const { people, isLoading, error } = useSelector((state) => state.faceEmbeddings);
  const tenantId   = localStorage.getItem("tenant_id");

  const [search,      setSearch]      = useState("");
  const [showAdd,     setShowAdd]     = useState(false);
  const [viewPerson,  setViewPerson]  = useState(null);
  const [editPerson,  setEditPerson]  = useState(null);
  const [deleteId,    setDeleteId]    = useState(null);

  useEffect(() => {
    if (tenantId) dispatch(listFaceEmbeddings({ tenantId }));
  }, [dispatch, tenantId]);

  const filtered = people.filter(
    (p) =>
      (p.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (p.employee_id || "").toLowerCase().includes(search.toLowerCase())
  );

  const handleAdd = (form) => {
    const formData = new FormData();
    formData.append("name", form.name);
    formData.append("employee_id", form.registerId);
    if (form.email) formData.append("email", form.email);
    if (form.phone) formData.append("phone_number", form.phone);
    form.photos.forEach((file) => formData.append("files", file));
    dispatch(enrollFace({ tenantId, formData })).then((result) => {
      if (enrollFace.rejected.match(result)) {
        toast.error(result.payload || "Failed to enroll person.");
      } else {
        toast.success("Person enrolled successfully.");
        dispatch(listFaceEmbeddings({ tenantId }));
      }
    });
  };

  const handleEdit = async (updated) => {
    // 1. Update metadata (PUT /{id})
    const metaResult = await dispatch(updateFaceEmbeddingMeta({
      tenantId,
      faceEmbeddingId: updated.id,
      data: {
        name:         updated.name,
        employee_id:  updated.registerId,
        email:        updated.email  || null,
        phone_number: updated.phone  || null,
      },
    }));
    if (updateFaceEmbeddingMeta.rejected.match(metaResult)) {
      toast.error(metaResult.payload || "Failed to update person.");
      return;
    }

    const newFiles      = updated.photos      || [];
    const remainingRefs = updated.remainingRefs || [];
    const originalRefs  = (updated.source_image_refs || []);
    const photosChanged = newFiles.length > 0 || remainingRefs.length !== originalRefs.length;

    // 2. Update photos only if something changed (PUT /{id}/photos)
    // Backend replaces ALL photos, so fetch bytes of kept existing ones + add new files
    if (photosChanged) {
      const formData = new FormData();

      // Fetch bytes for existing photos the user kept
      for (const ref of remainingRefs) {
        try {
          const url = buildFaceImageUrl(ref, tenantId);
          const res = await fetch(url, { credentials: "include" });
          if (res.ok) {
            const blob = await res.blob();
            const fname = ref.split("/").pop() || "photo.jpg";
            formData.append("files", new File([blob], fname, { type: "image/jpeg" }));
          }
        } catch (_) {}
      }

      // Append newly uploaded files
      newFiles.forEach((file) => formData.append("files", file));

      if ([...formData.getAll("files")].length === 0) {
        toast.error("No valid photos to save.");
        return;
      }

      const photoResult = await dispatch(updateFaceEmbeddingPhotos({
        tenantId,
        faceEmbeddingId: updated.id,
        formData,
      }));
      if (updateFaceEmbeddingPhotos.rejected.match(photoResult)) {
        toast.error(photoResult.payload || "Failed to update photos.");
        return;
      }
    }

    toast.success("Person updated successfully.");
    dispatch(clearSelected());
    dispatch(listFaceEmbeddings({ tenantId }));
  };

  const handleDelete = (id) => {
    dispatch(deleteFaceEmbedding({ tenantId, faceEmbeddingId: id }));
    setDeleteId(null);
  };

  return (
    <div className="p-6 min-h-screen" style={{ background: colors.bg }}>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: colors.text }}>People Management</h1>
          <p className="text-sm mt-0.5" style={{ color: colors.textDim }}>Manage registered people, IDs, and reference photos.</p>
        </div>
        <button onClick={() => setShowAdd(true)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm ${buttons.primary}`}>
          <PlusIcon size={16} />
          Add People
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 px-4 py-3 rounded-xl text-sm" style={{ backgroundColor: `${colors.danger}15`, border: `1px solid ${colors.danger}`, color: colors.danger }}>
          {error}
        </div>
      )}

      {/* Search + count */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-sm">
          <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: colors.textMute }}>
            <SearchIcon size={15} />
          </span>
          <input
            className="w-full pl-9 pr-4 py-2 rounded-xl text-sm outline-none transition-all"
            style={{ backgroundColor: colors.panel, border: `1px solid ${colors.border}`, color: colors.text }}
            placeholder="Search by name or ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={onFocus} onBlur={onBlur}
          />
        </div>
        <span className="text-sm font-semibold" style={{ color: colors.textDim }}>{filtered.length} people</span>
      </div>

      {/* Table */}
      <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: colors.panel, border: `1px solid ${colors.border}`, boxShadow: shadows.card }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
              {["S.NO", "NAME", "REGISTER NUMBER / ID", "EMAIL", "PHONE", "ACTIONS"].map((h) => (
                <th key={h} className="px-5 py-3 text-left text-xs font-semibold tracking-wider" style={{ color: colors.textMute }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((person, idx) => (
              <tr
                key={person.id}
                className="transition-colors"
                style={{ borderBottom: `1px solid ${colors.border}` }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.bg)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
              >
                <td className="px-5 py-3.5 font-medium" style={{ color: colors.textMute }}>{idx + 1}</td>
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ background: AVATAR_COLORS[idx % AVATAR_COLORS.length] }}>
                      {(person.name || "?").split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("")}
                    </div>
                    <span className="font-semibold" style={{ color: colors.text }}>{person.name}</span>
                  </div>
                </td>
                <td className="px-5 py-3.5 font-mono text-xs" style={{ color: colors.textMute }}>{person.employee_id}</td>
                <td className="px-5 py-3.5" style={{ color: colors.textDim }}>{person.email        || "—"}</td>
                <td className="px-5 py-3.5" style={{ color: colors.textDim }}>{person.phone_number || "—"}</td>
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-2">
                    {[
                      { tip: "View",   icon: <EyeIcon size={14} />,   onClick: () => setViewPerson(person), danger: false },
                      { tip: "Edit",   icon: <EditIcon size={14} />,  onClick: () => setEditPerson(person), danger: false },
                      { tip: "Delete", icon: <TrashIcon size={14} />, onClick: () => setDeleteId(person.id), danger: true },
                    ].map(({ tip, icon, onClick, danger }) => (
                      <Tooltip key={tip} title={tip} placement="top">
                        <button
                          onClick={onClick}
                          className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors"
                          style={{ border: `1px solid ${colors.border}`, color: colors.textMute }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = danger ? colors.danger : colors.primary; e.currentTarget.style.borderColor = danger ? colors.danger : colors.primary; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = colors.textMute; e.currentTarget.style.borderColor = colors.border; }}
                        >
                          {icon}
                        </button>
                      </Tooltip>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-sm" style={{ color: colors.textMute }}>Loading...</td>
              </tr>
            )}
            {!isLoading && filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-sm" style={{ color: colors.textMute }}>No people found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {showAdd && <AddPersonModal onClose={() => setShowAdd(false)} onAdd={handleAdd} />}

      {viewPerson && (
        <ViewPersonModal
          person={viewPerson}
          onClose={() => setViewPerson(null)}
          onEditOpen={(p) => setEditPerson(p)}
        />
      )}

      {editPerson && (
        <EditPersonModal
          person={editPerson}
          onClose={() => setEditPerson(null)}
          onEdit={handleEdit}
          tenantId={tenantId}
        />
      )}

      {deleteId !== null && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center ${bgcolors.overlay} backdrop-blur-sm p-4`}>
          <div className="w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden text-center" style={{ backgroundColor: colors.panel, border: `1px solid ${colors.border}` }}>
            <div className="h-1 w-full" style={{ background: gradients.accent }} />
            <div className="p-6">
              <h2 className="text-lg font-bold mb-2" style={{ color: colors.text }}>Delete Person</h2>
              <p className="text-sm mb-5" style={{ color: colors.textDim }}>Are you sure you want to remove this person? This action cannot be undone.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteId(null)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors"
                  style={{ backgroundColor: colors.bg2, color: colors.textDim, border: `1px solid ${colors.border}` }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.border)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = colors.bg2)}
                >
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteId)} className={`flex-1 py-2.5 rounded-xl text-sm ${buttons.danger}`}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
